import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { publishErrorNotification } from '@nbx/frontend-helpers/publish';
import { CSVLink } from 'react-csv';
import Row from 'components/shared/Row';
import Column from 'components/shared/Column';
import Text from 'components/shared/Text';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import Select from 'components/shared/Select';
import ERRORS from 'constants/errors';
import i18n from 'i18next';

// Table Parts
import TableLoading from './TableLoading';
import TableLoadingPage from './TableLoadingPage';
import TableError from './TableError';
import TableEmpty from './TableEmpty';
import TableDateFilter from './TableDateFilter';
import styles from './styles.module.scss';

/* 
  This is a shot at creating a virtualized table,
  If we need more advanced features, consider using:
  https://github.com/bvaughn/react-window
*/

const EVENT_OPTS = {
  passive: true,
  capture: true
};

export function Table(props) {
  const [isInitialLoading, setInitialLoading] = useState(true);
  const [height, setHeight] = useState(0);
  const [bodyOffset, setBodyOffset] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);
  const tableBody = useRef(null);
  const csvLinkRef = useRef(null);

  useEffect(() => {
    if (isInitialLoading && !props.isLoading) setInitialLoading(false);
  }, [isInitialLoading, props.isLoading]);

  useEffect(() => {
    const resize = () => {
      let newHeight;
      if (props.hasMaxHeight && tableBody.current) newHeight = tableBody.current.offsetHeight;
      else newHeight = window.innerHeight || document.documentElement.offsetHeight;
      if (newHeight !== height) setHeight(newHeight);
    };

    const scroll = () => {
      if (tableBody.current) {
        setBodyOffset(Math.max(0, -tableBody.current.getBoundingClientRect().top || 0));
        setScrollOffset(tableBody.current.scrollTop);
      }
    };

    const tableBodyRef = tableBody.current;
    if (props.data.length > 0) {
      resize();
      scroll();

      window.addEventListener('resize', resize, EVENT_OPTS);
      if (props.hasMaxHeight && tableBodyRef) tableBodyRef.addEventListener('scroll', scroll, EVENT_OPTS);
      else window.addEventListener('scroll', scroll, EVENT_OPTS);
    }

    return () => {
      window.removeEventListener('resize', resize, EVENT_OPTS);
      window.removeEventListener('scroll', scroll, EVENT_OPTS);
      if (tableBodyRef) tableBodyRef.removeEventListener('scroll', scroll, EVENT_OPTS);
    };
  }, [props.data, props.hasMaxHeight, height]);

  let totalHeight; // height of all rows
  let start = 0; // first visible row index
  let visibleRowCount = 0; // actual number of visible rows (without overscan)

  if (!props.hasMaxHeight) {
    totalHeight = `${props.rowHeight * props.data.length}px`;
    if (props.pausedForEmptyPages) totalHeight = `${props.rowHeight * (props.data.length + 1)}px`;
    start = (bodyOffset / props.rowHeight) | 0; // eslint-disable-line no-bitwise

    visibleRowCount = (height / props.rowHeight) | 0; // eslint-disable-line no-bitwise
  } else {
    start = ((bodyOffset + scrollOffset) / props.rowHeight) | 0; // eslint-disable-line no-bitwise
  }

  // Overscan: render blocks of rows modulo an overscan row count
  // This dramatically reduces DOM writes during scrolling
  if (props.overscan) {
    start = Math.max(0, start - (start % props.overscan));
    visibleRowCount += props.overscan;
  }

  // last visible + overscan row index
  const end = start + 1 + visibleRowCount;
  if (typeof props.setRefreshEndIndex === 'function') {
    props.setRefreshEndIndex(end);
  }

  // children currently in viewport plus overscan items
  const visibleRows = props.data.slice(start, end);
  const afterEndRowCount = Math.max(0, props.data.length - end); // number of rows below end that will be clipped

  if (
    props.hasNextPage &&
    !props.pausedForEmptyPages &&
    end > props.data.length - props.overscan &&
    !props.isLoadingNextPage &&
    !props.isLoadingAllPages
  ) {
    if (typeof props.fetchNextPage === 'function') props.fetchNextPage();
  }

  const getAllPagesForDownload = async () => {
    try {
      await props.fetchAllPages();
      csvLinkRef.current.link.click();
    } catch (error) {
      publishErrorNotification({ message: ERRORS.fetchAllPages }, error);
    }
  };

  const rendersFiltersRow = () => {
    if (!props.viewOptions && !props.downloadable) return null;
    return (
      <Row styles={[styles.filtersAndFunctionsRow]}>
        <div className={styles.filtersContainer}>
          {props.dateFiltering && renderDateFilters()}
          {props.viewOptions && renderViewSelector()}
        </div>
        {props.downloadable && renderDownloadButtons()}
      </Row>
    );
  };

  const renderDateFilters = () => {
    return (
      <TableDateFilter
        dateFrom={props.dateFrom}
        onDateFromChange={props.onDateFromChange}
        dateTo={props.dateTo}
        onDateToChange={props.onDateToChange}
      />
    );
  };

  const renderViewSelector = () => {
    return (
      <Select
        id="tableview"
        name="Table View"
        value={props.view}
        onChange={props.onViewChange}
        options={props.viewOptions}
        labelStyles={[styles.hiddenLabel]}
        wrapperStyles={[styles.viewOptionSelect]}
      />
    );
  };

  const renderDownloadButtons = () => {
    if (!props.hasNextPage) {
      return (
        <CSVLink
          data={props.data}
          headers={props.csvHeaders}
          filename={props.downloadFilename}
          ref={csvLinkRef}
          separator=";">
          <ButtonPrimary
            disabled={
              props.data.length === 0 ||
              props.isLoading ||
              props.isLoadingAllPages ||
              props.error ||
              props.isLoadingNextPage
            }>
            {props.downloadButtonLabel}
          </ButtonPrimary>
        </CSVLink>
      );
    }
    return (
      <ButtonPrimary
        onClick={getAllPagesForDownload}
        disabled={
          props.data.length === 0 || props.isLoading || props.isLoadingAllPages || props.isLoadingNextPage
        }
        loading={props.isLoadingAllPages}>
        {props.downloadButtonLabel}
      </ButtonPrimary>
    );
  };

  const waitingForPageWithData = props.isLoadingNextPage && props.data.length === 0;
  const loadingFull = props.isLoading && !props.error;
  const loadingNextPage = (props.isLoadingAllPages || props.isLoadingNextPage) && !loadingFull;
  const hasLinkAndActions = !!props.renderLink && props.hasActions;

  const renderRows = () => {
    return visibleRows.map((item, rowIndex) => {
      const columns = props.columnWidths.map((columnWidth, index) => {
        return (
          <Column
            lg={columnWidth}
            styles={[
              {
                [styles.startAligned]: index < props.endAlignIndex,
                [styles.endAligned]: index >= props.endAlignIndex,
                [styles.linkActionColumn]: hasLinkAndActions && index === props.columnWidths.length - 1
              },
              ...props.cellStyles
            ]}
            key={index} // eslint-disable-line
          >
            {props.renderItem(item, index)}
          </Column>
        );
      });
      if (hasLinkAndActions) {
        return (
          <Row
            border={props.borders && rowIndex !== visibleRows.length - 1}
            styles={[
              styles.tableRow,
              {
                [styles.highlighted]: rowIndex === props.highlightRowIndex,
                [styles.highlightOnHover]: props.highlightRowOnHover
              },
              ...props.rowStyles
            ]}
            style={{ height: `${props.rowHeight}px` }}
            key={props.keyField ? item[props.keyField] : rowIndex}>
            <Row
              styles={[styles.nonActionColumns, ...props.rowStyles]}
              to={typeof props.renderLink === 'function' ? props.renderLink(item) : null}
              onClick={
                typeof props.onClickRow === 'function'
                  ? () => {
                      props.onClickRow(item);
                    }
                  : null
              }>
              {columns.slice(0, -1)}
            </Row>
            {columns.slice(-1, columns.length)}
          </Row>
        );
      }
      return (
        <Row
          border={props.borders && rowIndex !== visibleRows.length - 1}
          styles={[
            styles.tableRow,
            {
              [styles.highlighted]: rowIndex === props.highlightRowIndex,
              [styles.highlightOnHover]: props.highlightRowOnHover
            },
            ...props.rowStyles
          ]}
          to={typeof props.renderLink === 'function' ? props.renderLink(item) : null}
          onClick={
            typeof props.onClickRow === 'function'
              ? () => {
                  props.onClickRow(item);
                }
              : null
          }
          style={{ height: `${props.rowHeight}px` }}
          key={props.keyField ? item[props.keyField] : rowIndex}>
          {columns}
        </Row>
      );
    });
  };

  const renderTableContents = () => {
    const errorMessage = i18n.t('error_title_generic');
    if (props.error)
      return (
        <TableError
          message={props.errorMessage || errorMessage || 'Something went wrong'}
          refetchData={props.refetchData}
          refetchMessage={i18n.t('common_please_try_again') || 'Please try again'}
        />
      );
    if (isInitialLoading || waitingForPageWithData) return null;
    if (props.pausedForEmptyPages && props.data.length === 0) {
      return (
        <div className={styles.fetchMoreContainer} style={{ height: props.hasMaxHeight ? '100%' : '250px' }}>
          <ButtonPrimary onClick={props.fetchNextPage}>{i18n.t('common_get_more')}</ButtonPrimary>
        </div>
      );
    }
    if (props.data.length === 0 && !props.hasNextPage) {
      const emptyTitle = i18n.t('common_nothing_to_show');
      return (
        <TableEmpty
          title={props.emptyTitle || emptyTitle || 'Nothing to show here yet'}
          message={props.emptyMessage}
          hasMaxHeight={props.hasMaxHeight}
        />
      );
    }
    return (
      <div className={classnames([styles.tableContentWrapper, props.tableStyles])}>
        <Row border={props.headerBorder} styles={[styles.headerRow, ...props.headerStyles]}>
          {props.headers.map((header, index) => {
            const isFunction = typeof header === 'function';
            return (
              <Column
                lg={props.columnWidths[index]}
                styles={[
                  styles.headerColumn,
                  {
                    [styles.startAligned]: index < props.endAlignIndex,
                    [styles.endAligned]: index >= props.endAlignIndex
                  }
                ]}
                key={isFunction ? index : header}>
                {isFunction ? header() : <Text muted>{header}</Text>}
              </Column>
            );
          })}
        </Row>
        <div
          className={classnames([
            styles.tableBodyWrapper,
            { [styles.tableBodyWrapperWithMaxHeight]: props.hasMaxHeight },
            { [styles.blackout]: props.onPureBackground }
          ])}>
          <div
            className={classnames([
              styles.tableBody,
              { [styles.tableBodyWithMaxHeight]: props.hasMaxHeight }
            ])}
            style={{ height: props.hasMaxHeight ? '100%' : totalHeight }}
            ref={tableBody}>
            <div className={styles.tableRows} style={{ top: start * props.rowHeight }}>
              {renderRows()}
              {props.hasMaxHeight && loadingNextPage && !waitingForPageWithData && <TableLoadingPage />}
              {props.pausedForEmptyPages && props.data.length === 0 && (
                <div className={styles.fetchMoreContainer} style={{ height: `${props.rowHeight}px` }}>
                  <ButtonPrimary onClick={props.fetchNextPage}>{i18n.t('common_get_more')}</ButtonPrimary>
                </div>
              )}
              <div style={{ height: `${afterEndRowCount * props.rowHeight}px` }} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.tableWrap}>
      {rendersFiltersRow()}
      <div className={styles.tableContainer}>
        {renderTableContents()}
        {(loadingFull || waitingForPageWithData) && (
          <TableLoading
            isInitialLoading={isInitialLoading || waitingForPageWithData}
            hasMaxHeight={props.hasMaxHeight}
          />
        )}
        {!props.hasMaxHeight && loadingNextPage && !waitingForPageWithData && <TableLoadingPage />}
      </div>
    </div>
  );
}

/* eslint-disable react/no-unused-prop-types */
// Hitting a bug in eslint marking props used in functions as never used
Table.propTypes = {
  borders: PropTypes.bool,
  data: PropTypes.array.isRequired,
  keyField: PropTypes.string,
  headers: PropTypes.array.isRequired,
  headerBorder: PropTypes.bool,
  endAlignIndex: PropTypes.number,
  columnWidths: PropTypes.array.isRequired,
  overscan: PropTypes.number,
  // loading States
  isLoading: PropTypes.bool,
  isLoadingNextPage: PropTypes.bool,
  isLoadingAllPages: PropTypes.bool,
  // fetching functions
  hasNextPage: PropTypes.bool,
  pausedForEmptyPages: PropTypes.bool,
  fetchNextPage: PropTypes.func,
  fetchAllPages: PropTypes.func,
  refetchData: PropTypes.func,
  // errors
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  // empty tables
  emptyTitle: PropTypes.string,
  emptyMessage: PropTypes.string,
  // View Filtering
  viewOptions: PropTypes.object,
  view: PropTypes.string,
  onViewChange: PropTypes.func,
  // Date Filtering
  dateFiltering: PropTypes.bool,
  dateFrom: PropTypes.instanceOf(Date),
  onDateFromChange: PropTypes.func,
  dateTo: PropTypes.instanceOf(Date),
  onDateToChange: PropTypes.func,
  // CSV downloads
  downloadable: PropTypes.bool,
  csvHeaders: PropTypes.array,
  downloadFilename: PropTypes.string,
  downloadButtonLabel: PropTypes.string,
  // rendering
  renderItem: PropTypes.func.isRequired,
  renderLink: PropTypes.func,
  hasActions: PropTypes.bool,
  rowHeight: PropTypes.number,
  highlightRowIndex: PropTypes.number,
  highlightRowOnHover: PropTypes.bool,
  hasMaxHeight: PropTypes.bool, // if your custom styles specify a max height for the table, set this flag
  onPureBackground: PropTypes.bool, // née blackout
  onClickRow: PropTypes.func,
  setRefreshEndIndex: PropTypes.func,
  // custom styles for various table bits
  headerStyles: PropTypes.array,
  tableStyles: PropTypes.array,
  rowStyles: PropTypes.array,
  cellStyles: PropTypes.array
};

Table.defaultProps = {
  borders: true,
  keyField: undefined,
  isLoading: false,
  isLoadingNextPage: false,
  isLoadingAllPages: false,
  hasNextPage: false,
  pausedForEmptyPages: false,
  fetchNextPage: () => {},
  fetchAllPages: () => {},
  refetchData: () => {},
  error: false,
  errorMessage: undefined,
  emptyTitle: undefined,
  emptyMessage: undefined,
  endAlignIndex: 1000,
  headerBorder: false,
  overscan: 5,
  renderLink: undefined,
  hasActions: false,
  rowHeight: 66,
  highlightRowIndex: undefined,
  highlightRowOnHover: false,
  hasMaxHeight: false,
  onPureBackground: false,
  onClickRow: undefined,
  viewOptions: undefined,
  view: undefined,
  onViewChange: undefined,
  dateFiltering: false,
  dateFrom: undefined,
  onDateFromChange: undefined,
  dateTo: undefined,
  onDateToChange: undefined,
  downloadable: false,
  csvHeaders: undefined,
  downloadFilename: undefined,
  downloadButtonLabel: undefined,
  setRefreshEndIndex: null,
  headerStyles: [],
  tableStyles: [],
  rowStyles: [],
  cellStyles: []
};

export default memo(Table);
